class ProductFlavours extends HTMLElement {
  constructor() {
    super();

    this._onPlusClick = this._onPlusClick.bind(this);
    this._onMinusClick = this._onMinusClick.bind(this);

    this._variantWatchRAF = null;
    this._lastVariantId = null;

    this._limitMap = null; // { "variantId": number }
    this._variantById = new Map(); // from /products/<handle>.js fallback
  }

  async connectedCallback() {
    this._bindUI();

    // 1) Load per-product limits from Liquid JSON (most reliable)
    this._loadLimitMapFromLiquid();

    // 2) Fallback: load product JSON (only used if limit map missing)
    await this._loadProductDataFallback();

    // Watch live variant changes
    this._startVariantWatcher();

    // Initial sync
    this._syncLimitFromCurrentVariant();
    this._enforceLimitAndRefreshUI();
  }

  disconnectedCallback() {
    this._unbindUI();
    this._stopVariantWatcher();
  }

  /* ---------------------------
   * UI bindings
   * -------------------------*/
  _bindUI() {
    this.querySelectorAll('.flavour-plus').forEach((btn) => {
      btn.addEventListener('click', this._onPlusClick);
    });

    this.querySelectorAll('.flavour-minus').forEach((btn) => {
      btn.addEventListener('click', this._onMinusClick);
    });
  }

  _unbindUI() {
    this.querySelectorAll('.flavour-plus').forEach((btn) => {
      btn.removeEventListener('click', this._onPlusClick);
    });

    this.querySelectorAll('.flavour-minus').forEach((btn) => {
      btn.removeEventListener('click', this._onMinusClick);
    });
  }

  _onPlusClick(e) {
    e.preventDefault();
    if (!this._canAddOne()) return;

    const input = e.currentTarget.parentElement?.querySelector('.flavour_component_product_qty_input');
    if (!input) return;

    input.value = String((parseInt(input.value || '0', 10) || 0) + 1);
    this._setHiddenValueAndUI();
  }

  _onMinusClick(e) {
    e.preventDefault();

    const input = e.currentTarget.parentElement?.querySelector('.flavour_component_product_qty_input');
    if (!input) return;

    const current = parseInt(input.value || '0', 10) || 0;
    if (current <= 0) return;

    input.value = String(current - 1);
    this._setHiddenValueAndUI();
  }

  /* ---------------------------
   * Variant detection (robust)
   * -------------------------*/
  _getVariantIdInput() {
    const localForm = this.closest('form[action*="/cart/add"]');
    const localId = localForm?.querySelector('input[name="id"]');
    if (localId) return localId;

    const form = document.querySelector('form[action*="/cart/add"]');
    return form?.querySelector('input[name="id"]') || null;
  }

  _getSelectedVariantId() {
    return this._getVariantIdInput()?.value || null;
  }

  _startVariantWatcher() {
    const tick = () => {
      if (!this.isConnected) return;

      const id = this._getSelectedVariantId();
      if (id && id !== this._lastVariantId) {
        this._lastVariantId = id;
        this._syncLimitFromCurrentVariant();
        this._enforceLimitAndRefreshUI();
      }

      this._variantWatchRAF = requestAnimationFrame(tick);
    };

    cancelAnimationFrame(this._variantWatchRAF);
    this._variantWatchRAF = requestAnimationFrame(tick);
  }

  _stopVariantWatcher() {
    cancelAnimationFrame(this._variantWatchRAF);
    this._variantWatchRAF = null;
  }

  /* ---------------------------
   * Limits from Liquid (best)
   * -------------------------*/
  _loadLimitMapFromLiquid() {
    const productId = this.dataset.productId;
    if (!productId) return;

    const el = document.getElementById(`variant-flavour-limits-${productId}`);
    if (!el) return;

    try {
      const parsed = JSON.parse(el.textContent || '{}');
      this._limitMap = parsed && typeof parsed === 'object' ? parsed : null;
    } catch (e) {
      console.error('ProductFlavours: failed to parse variant limit map JSON', e);
      this._limitMap = null;
    }
  }

  /* ---------------------------
   * Product JSON fallback
   * -------------------------*/
  async _loadProductDataFallback() {
    // Only needed if the Liquid map isn't present for some reason
    try {
      const handle = this._getProductHandleFromURL();
      if (!handle) return;

      const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
      if (!res.ok) return;

      const product = await res.json();
      if (!Array.isArray(product?.variants)) return;

      this._variantById.clear();
      product.variants.forEach((v) => {
        if (v?.id) this._variantById.set(String(v.id), v);
      });
    } catch (err) {
      // Not fatal
      console.error('ProductFlavours: fallback product load failed', err);
    }
  }

  _getProductHandleFromURL() {
    const m = window.location.pathname.match(/\/products\/([^\/]+)/i);
    return m ? decodeURIComponent(m[1]) : null;
  }

  _parseBoxQty(text) {
    if (!text) return null;
    const m = text.match(/box\s*of\s*(\d+)/i);
    if (!m) return null;
    const n = parseInt(m[1], 10);
    return Number.isFinite(n) ? n : null;
  }

  _syncLimitFromCurrentVariant() {
    const variantId = this._getSelectedVariantId();
    if (!variantId) return;

    // 1) Best: metafield-driven map from Liquid
    const mapped = this._limitMap?.[String(variantId)];
    if (typeof mapped === 'number' && Number.isFinite(mapped) && mapped > 0) {
      this._applyLimit(mapped);
      return;
    }

    // 2) Fallback: parse from variant title via product JSON
    const variant = this._variantById.get(String(variantId));
    const title = (variant?.title || '').trim();
    const parsed = this._parseBoxQty(title);

    if (parsed != null && parsed > 0) {
      this._applyLimit(parsed);
      return;
    }

    // 3) Final fallback: DO NOT break selection.
    // Keep whatever limit was already on the element (from initial Liquid render).
    const current = this._limit();
    if (current > 0) {
      this._applyLimit(current);
    }
  }

  _applyLimit(limit) {
    this.setAttribute('data-qty', String(limit));
    this.dataset.qty = String(limit);
    this._updateLimitUI(limit);
  }

  _updateLimitUI(limit) {
    // Update message number
    const limitSpan = this.querySelector('.flavour-limit-text');
    if (limitSpan) limitSpan.textContent = String(limit);

    // Update max attribute only when limit > 0
    this.querySelectorAll('.flavour_component_product_qty_input').forEach((inp) => {
      if (limit > 0) inp.max = String(limit);
      else inp.removeAttribute('max');
    });
  }

  /* ---------------------------
   * Quantity + UI logic
   * -------------------------*/
  _limit() {
    const qty = parseInt(this.getAttribute('data-qty') || this.dataset.qty || '0', 10);
    return Number.isFinite(qty) ? qty : 0;
  }

  _currentTotal() {
    let total = 0;
    this.querySelectorAll('.flavour_component_product_qty_input').forEach((inp) => {
      total += parseInt(inp.value || '0', 10) || 0;
    });
    return total;
  }

  _canAddOne() {
    const limit = this._limit();
    if (!limit) return true;
    return this._currentTotal() < limit;
  }

  _enforceLimitAndRefreshUI() {
    const limit = this._limit();

    if (limit && this._currentTotal() > limit) {
      this.querySelectorAll('.flavour_component_product_qty_input').forEach((inp) => (inp.value = '0'));
    }

    this._setHiddenValueAndUI();
  }

  _setHiddenValueAndUI() {
    const values = [];
    this.querySelectorAll('.flavour').forEach((flavour) => {
      const inp = flavour.querySelector('.flavour_component_product_qty_input');
      const qty = parseInt(inp?.value || '0', 10) || 0;
      if (qty > 0) values.push(`${flavour.dataset.name} x ${qty}`);
    });

    const hidden = document.querySelector('.flavours-hidden-input');
    if (hidden) hidden.value = values.join(' ,');

    this._activeBtnCheck();
  }

  _activeBtnCheck() {
    const limit = this._limit();
    const total = this._currentTotal();

    const submitBtn =
      document.querySelector('.product-form__submit') ||
      document.querySelector('form[action*="/cart/add"] [type="submit"]');

    const alert = this.querySelector('.flavour_alert');
    if (!submitBtn) return;

    if (limit && total < limit) {
      submitBtn.classList.add('has_flavours');
      alert?.classList.add('alert-active');
    } else {
      submitBtn.classList.remove('has_flavours');
      alert?.classList.remove('alert-active');
    }
  }
}

customElements.define('product-flavours', ProductFlavours);